import { concat } from '@ember/helper';
import Component from '@glimmer/component';
import includes from '@nullvoxpopuli/ember-composable-helpers/helpers/includes';
import changesetGet from 'ember-changeset/helpers/changeset-get';
import t from 'ember-intl/helpers/t';
import { not } from 'ember-truth-helpers';
import and from 'ember-truth-helpers/helpers/and';
import EditionForm from 'parog-web/components/comorbidity/edition-form';
import { FurField } from 'parog-web/components/enrollment/fields';
import Provider from 'parog-web/components/forc/provider';
import AccordionBlock from 'parog-web/components/medical-history/accordion-block';
import type Patient from 'parog-web/models/patient';
import DiabetoDt2Diagnostic from './dt2/diagnostic-fields';
import type MedicalHistoryFormComponent from '../diabeto';

interface Signature {
  Args: {
    patient: Patient;
    forcComponent: MedicalHistoryFormComponent;
  };
}

export default class extends Component<Signature> {
  get isGeneric() {
    return (
      this.args.forcComponent.enrollmentChangeset.get('followUpReason') ==
      'diabeto-generic'
    );
  }

  get shouldDisplayFurSelection() {
    // display FuR selection only when the patient's therapeutical has several follow up reason options
    return (this.therapeuticalArea?.followUpReasons?.length ?? 0) > 1;
  }

  get therapeuticalArea() {
    return this.args.patient.followUpReasonRecord?.therapeuticalArea;
  }
  <template>
    <Provider @forcComponent={{@forcComponent}} @patient={{@patient}}>
      <:content as |Form|>
        {{#if @patient.isEnrolled}}
          <AccordionBlock @title='Patient suivi pour' @isActive={{true}}>
            <div class='field'>
              {{t
                (concat
                  'therapeutical-area.'
                  @patient.followUpReasonRecord.therapeuticalArea.id
                )
              }}
              :
              {{t
                (concat
                  'follow-up-reason.'
                  @forcComponent.enrollmentChangeset.followUpReason
                )
              }}
              {{#if this.shouldDisplayFurSelection}}
                <FurField
                  @changeset={{@forcComponent.enrollmentChangeset}}
                  @therapeuticalArea={{this.therapeuticalArea}}
                />
              {{/if}}

            </div>
          </AccordionBlock>

          <AccordionBlock
            @title='Détails du
        suivi'
            @isActive={{true}}
            class='pt-[1em]'
          >
            <DiabetoDt2Diagnostic
              @forcComponent={{@forcComponent}}
              @form={{Form}}
              @patient={{@patient}}
              @isMandatory={{not this.isGeneric}}
            />
          </AccordionBlock>

          <AccordionBlock @title='Traitements en cours' @isActive={{true}}>
            <div class='field'>
              <label>Metformine {{unless this.isGeneric '*'}}</label>
              <div class='fields inline'>
                <Form.BasicRadioGroup @propertyName='met' as |Group|>
                  <Group.FieldRadio @label='Oui' @value={{true}} />
                  <Group.FieldRadio @label='Non' @value={{false}} />
                </Form.BasicRadioGroup>
              </div>
            </div>
            <div class='field'>
              <label>iSGLT2 {{unless this.isGeneric '*'}}</label>
              <div class='fields inline'>
                <Form.BasicRadioGroup @propertyName='isglt2' as |Group|>
                  <Group.FieldRadio @label='Oui' @value={{true}} />
                  <Group.FieldRadio @label='Non' @value={{false}} />
                </Form.BasicRadioGroup>
              </div>
            </div>
            <div class='field'>
              <label>AR GLP-1 {{unless this.isGeneric '*'}}</label>
              <div class='fields inline'>
                <Form.BasicRadioGroup @propertyName='arglp1' as |Group|>
                  <Group.FieldRadio @label='Oui' @value={{true}} />
                  <Group.FieldRadio @label='Non' @value={{false}} />
                </Form.BasicRadioGroup>
              </div>
            </div>
            {{#if
              (and
                (changesetGet @forcComponent.changeset 'isglt2')
                (changesetGet @forcComponent.changeset 'arglp1')
              )
            }}
              <div class='mb-[1em]' data-test-not-eligible>
                <strong>
                  Au regard des critères d'exclusion, ce patient n'est pas
                  éligible.
                </strong>
              </div>
            {{/if}}
            <div class='field'>
              <label>Insuline {{unless this.isGeneric '*'}}</label>
              <div class='fields inline'>
                <Form.BasicRadioGroup @propertyName='insulin' as |Group|>
                  <Group.FieldRadio @label='Oui' @value={{true}} />
                  <Group.FieldRadio @label='Non' @value={{false}} />
                </Form.BasicRadioGroup>
              </div>
            </div>

            <div class='field'>
              <label>iDPP4 {{unless this.isGeneric '*'}}</label>
              <div class='fields inline'>
                <Form.BasicRadioGroup @propertyName='idpp4' as |Group|>
                  <Group.FieldRadio @label='Oui' @value={{true}} />
                  <Group.FieldRadio @label='Non' @value={{false}} />
                </Form.BasicRadioGroup>
              </div>
            </div>
          </AccordionBlock>

          <AccordionBlock
            @title='Comorbidités et allergies'
            @isActive={{true}}
            class='pt-[1em]'
          >
            <EditionForm
              @patient={{@patient}}
              @changeset={{@forcComponent.comorbiditiesChangeset}}
            >
              {{!-- prettier-ignore --}}
              <:comorbidityAdditionalInfo>
            {{#if (includes "N18.9" (changesetGet @forcComponent.comorbiditiesChangeset "comorbidities"))}}
              <div class="field">
                <label>Maladie Rénale Chronique (MRC)</label>
                <div class='grid grid-cols-[0.5fr_0.5fr] gap-[1em]'>
                  <div>
                    <div class="mb16 flex items-center justify-end gap-[1em] [&>label]:flex [&>label]:w-full [&>label]:justify-end [&>label]:text-end">
                      <Form.NumberInput @labelName="DFGe (formule CKD-EPI)" @propertyName="dfg" @isMandatory={{true}} />
                    </div>
                    <div class="mb16 flex items-center justify-end gap-[1em] [&>label]:flex [&>label]:w-full [&>label]:justify-end [&>label]:text-end">
                      <Form.NumberInput @labelName="RAC** (en mg/g)" @placeholder="150" @propertyName="rac" @isMandatory={{true}} />
                    </div>
                    <div class='flex items-center justify-end gap-[1em] [&>label]:flex [&>label]:w-full [&>label]:justify-end [&>label]:text-end'>
                      <Form.NumberInput @labelName="Albuminurie" @placeholder="40" @propertyName="albuminuria" @isMandatory={{true}} />
                    </div>
                  </div>
                  <div class='flex flex-col justify-around'>
                    <div class='italic text-[#808080]'>
                      *MRC = DGFe &lt; 60 ml/min/1,73m2 et / ou RAC &gt; ou égal
                      à 30mg/g*
                    </div>
                    <div class='italic text-[#808080]'>
                      ** Rapport albuminurie / créatinurie*
                    </div>
                    <div class='italic text-[#808080]'>
                      En mg/24h
                    </div>
                  </div>
                </div>
              </div>
            {{/if}}
            {{#if (includes "I50.9" (changesetGet @forcComponent.comorbiditiesChangeset "comorbidities"))}}
              <div class="field">
                <label>Insuffisance cardiaque (IC)</label>
                <div class='grid grid-cols-[0.5fr_0.5fr] gap-[1em]'>
                  <div class='flex items-center justify-end gap-[1em] [&>label]:flex [&>label]:w-full [&>label]:justify-end [&>label]:text-end'>
                    <Form.NumberInput @labelName="FEVG (en %)" @placeholder="40" @propertyName="fevg" @isMandatory={{true}} />
                  </div>
                </div>
              </div>
            {{/if}}

            <div class="field">
              <label>HTA</label>
              <div class='grid grid-cols-[0.5fr_0.5fr] gap-[1em]'>
                <div>
                  {{#if (includes "I10" (changesetGet @forcComponent.comorbiditiesChangeset "comorbidities"))}}
                    <div class="mb16 flex items-center justify-end gap-[1em] [&>label]:flex [&>label]:w-full [&>label]:justify-end [&>label]:text-end">
                      <label>
                        HTA traitée {{unless this.isGeneric '*'}}
                      </label>
                      <Form.BasicRadioGroup @propertyName="hta" as |Group|>
                        <Group.FieldRadio @label="Oui" @value="treated" class="mb0" />
                        <Group.FieldRadio @label="Non" @value="yes" />
                      </Form.BasicRadioGroup>
                    </div>
                  {{/if}}
                  <div class='flex items-center justify-end gap-[1em] [&>label]:flex [&>label]:w-full [&>label]:justify-end [&>label]:text-end'>
                    <Form.NumberInput @labelName="Dernière pression artérielle systolique en consultation" @propertyName="systolic" @isMandatory={{true}} />
                  </div>
                  <div class='flex items-center justify-end gap-[1em] [&>label]:flex [&>label]:w-full [&>label]:justify-end [&>label]:text-end'>
                    <Form.NumberInput @labelName="Dernière pression artérielle diastolique en consultation" @propertyName="diastolic" @isMandatory={{true}} />
                  </div>
                </div>
              </div>
            </div>
            <div class="field">
              <label>Dyslipidémie</label>
              <div class='grid grid-cols-[0.5fr_0.5fr] gap-[1em]'>
                <div>
                  {{#if (includes "E78.9" (changesetGet @forcComponent.comorbiditiesChangeset "comorbidities"))}}
                    <div class="mb16 flex items-center justify-end gap-[1em] [&>label]:flex [&>label]:w-full [&>label]:justify-end [&>label]:text-end">
                      <label>
                        Dyslipidémie traitée {{unless this.isGeneric '*'}}
                      </label>
                      <Form.BasicRadioGroup @propertyName="dyslipidemieTreated" as |Group|>
                        <Group.FieldRadio @label="Oui" @value={{true}} class="mb0" />
                        <Group.FieldRadio @label="Non" @value={{false}} />
                      </Form.BasicRadioGroup>
                    </div>
                  {{/if}}
                  <div class='flex items-center justify-end gap-[1em] [&>label]:flex [&>label]:w-full [&>label]:justify-end [&>label]:text-end'>
                    <Form.NumberInput @labelName="Dernier taux de LDLc (en g/l)" @placeholder="1,2" @propertyName="lastDyslipidemie" @isMandatory={{true}} />
                  </div>
                </div>
              </div>
            </div>
          </:comorbidityAdditionalInfo>
            </EditionForm>
          </AccordionBlock>
          <div class='mt-[2em] flex gap-[1em]'>
            <div class='field'>
              <label>
                Antécédents familiaux de maladie cardiovasculaire
              </label>
            </div>
            <div
              class='flex items-center justify-end gap-[1em] [&>label]:flex [&>label]:w-full [&>label]:justify-end [&>label]:text-end'
            >
              <Form.BasicRadioGroup
                @propertyName='familyIcBackground'
                as |Group|
              >
                <Group.FieldRadio
                  @label='Oui, précoce (< 55 ans)'
                  @value='under_55'
                />
                <Group.FieldRadio @label='Oui' @value='true' />
                <Group.FieldRadio
                  class='!mb-[1em]'
                  @label='Non'
                  @value='false'
                />
              </Form.BasicRadioGroup>
            </div>
          </div>

        {{/if}}
      </:content>
    </Provider>
  </template>
}
